import { useCallback, useMemo, useState } from 'react'
import { Select, SelectProps } from 'antd'
import { debounce } from 'lodash'
import { useQuery } from '@tanstack/react-query'
import { useMedplum } from '@medplum/react'
import { Patient } from '@medplum/fhirtypes'
import { PatientData_PatientCareDataFragment } from 'generated/graphql'
import { formatPrimaryName } from 'utils/names'
import { PatientSchema } from 'store/patient'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { fetchPatientDataPatient, searchPatientsByName } from 'services/patient/PatientFetchService'
import { QueryKeys } from 'utils/query.constants'
import { serviceTypeExtractor, serviceTypeIdExtractor } from 'store/helpers/patient.helpers'

const { Option } = Select

// AllowedPatient can be either the GraphQL fragment or the store's PatientSchema
type AllowedPatient = PatientData_PatientCareDataFragment | PatientSchema | null

type DisplayPatient = (AllowedPatient | Patient) & {
  matchServiceType?: boolean
  serviceType?: PatientData_PatientCareDataFragment['serviceType']
}

export interface SearchablePatientProps extends SelectProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (value: string, option: any) => void
  serviceTypeIds?: string[]
  serviceTypeNames?: string[]
  allowedPatients?: AllowedPatient[]
  onDataFetched?: (patients: PatientData_PatientCareDataFragment[]) => void
}

/**
 * SearchablePatient component allows users to search and select patients by name.
 * Uses Abby API-backed patient search for lightweight typeahead fallback.
 */
export default function SearchablePatient({
  onChange,
  serviceTypeIds,
  serviceTypeNames,
  allowedPatients,
  onDataFetched,
  ...otherProps
}: SearchablePatientProps): JSX.Element {
  const [patientName, setPatientName] = useState<string>('')
  // Track loading state for fetching full patient data on selection
  const [isLoadingSelection, setIsLoadingSelection] = useState(false)

  const medplum = useMedplum()
  const { patientDataQueries: useApi } = useFeatureFlags()

  // Filter allowedPatients by search text (client-side filtering)
  const clientFilteredPatients = useMemo((): AllowedPatient[] | null => {
    if (!allowedPatients) return null
    if (!patientName || patientName.length === 0) return allowedPatients

    const searchLower = patientName.toLowerCase()
    return allowedPatients.filter((patient) => {
      if (!patient) return false
      const formattedName = formatPrimaryName(patient.name)
      return formattedName?.toLowerCase().includes(searchLower) ?? false
    })
  }, [allowedPatients, patientName])

  // Enable server query when:
  // 1. We have actual search text (length > 0), AND
  // 2. Either we don't have allowedPatients OR client-side filtering returns 0 results
  const queryEnabled =
    patientName.length > 0 && (!allowedPatients || clientFilteredPatients?.length === 0)

  // Use Abby API patient search for lightweight fallback results
  const { data: searchResults, isLoading } = useQuery({
    queryKey: [QueryKeys.PATIENTS, 'search', patientName],
    queryFn: async (): Promise<Patient[]> => {
      return searchPatientsByName(patientName)
    },
    enabled: queryEnabled
  })

  const displayPatients = useMemo<(DisplayPatient | null)[]>(() => {
    let patients: (DisplayPatient | Patient | null)[] = []

    // Use pre-computed filtered allowedPatients when available
    if (clientFilteredPatients && clientFilteredPatients.length > 0) {
      patients = clientFilteredPatients
    }
    // Use query results when no allowedPatients provided OR when filtering returned 0 results
    else if (searchResults && searchResults.length > 0) {
      patients = searchResults

      // If we have allowedPatients, merge them with query results (query results first for relevance)
      if (allowedPatients && allowedPatients.length > 0) {
        const queryPatientIds = new Set(searchResults.map((p) => p.id).filter(Boolean))
        // Filter out allowed patients that are already in query results
        const uniqueAllowedPatients = allowedPatients.filter(
          (p) => p && p.id && !queryPatientIds.has(p.id)
        )
        // Put query results first (more relevant), then remaining allowed patients
        patients = [...searchResults, ...uniqueAllowedPatients]
      }
    }
    // Show all allowed patients when not searching
    else if (allowedPatients) {
      patients = allowedPatients
    }

    const requiredServiceTypeIds = new Set(
      (serviceTypeIds ?? []).map((id) => id.trim()).filter(Boolean)
    )
    const requiredServiceTypeNames = new Set(
      (serviceTypeNames ?? []).map((name) => name.trim().toLowerCase()).filter(Boolean)
    )

    if (requiredServiceTypeIds.size === 0 && requiredServiceTypeNames.size === 0) {
      return patients as (DisplayPatient | null)[]
    }

    const patientsWithServiceType = patients.map((patient) => {
      if (!patient) return patient as DisplayPatient | null

      const serviceType = (patient as DisplayPatient).serviceType
      const patientServiceTypeIds = serviceTypeIdExtractor(serviceType)
      const patientServiceTypeNames = serviceTypeExtractor(serviceType)
      const matchServiceType =
        patientServiceTypeIds.some((id) => requiredServiceTypeIds.has(id)) ||
        patientServiceTypeNames.some((name) =>
          requiredServiceTypeNames.has(name.trim().toLowerCase())
        )

      return {
        ...patient,
        serviceType,
        matchServiceType
      } as DisplayPatient
    })

    // Sort patients with matching service types to the top
    const sortedPatients = patientsWithServiceType.sort((a, b) => {
      if (!a || !b) return 0
      if (a.matchServiceType && !b.matchServiceType) return -1
      if (!a.matchServiceType && b.matchServiceType) return 1
      return 0
    })

    return sortedPatients
  }, [searchResults, serviceTypeIds, serviceTypeNames, allowedPatients, clientFilteredPatients])

  // Debounced search handler
  const debouncedSetPatientName = useMemo(
    () =>
      debounce((searchText: string): void => {
        if (searchText.length > 0) {
          setPatientName(searchText)
        }
      }, 400),
    []
  )
  const handleSearch = useCallback(
    (searchText: string) => {
      debouncedSetPatientName(searchText)
    },
    [debouncedSetPatientName]
  )

  const onPatientChange = async (value: string | string[], option: unknown): Promise<void> => {
    // In mode="multiple", value is an array; extract the last-added ID for data fetching
    const latestId = Array.isArray(value) ? value[value.length - 1] : value

    // Check if we already have full patient data from allowedPatients
    const existingFullPatient = allowedPatients?.find((p) => p?.id === latestId)

    if (existingFullPatient) {
      onChange?.(value as string, {
        ...(option as Record<string, unknown>),
        ref: existingFullPatient
      })
      return
    }

    if (!latestId) {
      onChange?.(value as string, option)
      return
    }

    // Fetch full patient data for the most recently selected patient
    setIsLoadingSelection(true)
    try {
      const result = await fetchPatientDataPatient(medplum, latestId, useApi)
      const fullPatient = result?.Patient as PatientData_PatientCareDataFragment | null

      if (fullPatient) {
        onDataFetched?.([fullPatient])
      }

      onChange?.(value as string, {
        ...(option as Record<string, unknown>),
        ref: fullPatient
      })
    } catch {
      // Still propagate the value even if the GraphQL fetch fails
      onChange?.(value as string, option)
    } finally {
      setIsLoadingSelection(false)
    }
  }

  return (
    <Select
      showSearch
      filterOption={false}
      loading={isLoading || isLoadingSelection}
      onSearch={handleSearch}
      onChange={onPatientChange}
      {...otherProps}
    >
      {displayPatients?.map((option) => (
        <Option
          key={option?.id}
          value={option?.id}
          disabled={Boolean(serviceTypeIds?.length) && !option?.matchServiceType}
        >
          {option?.name ? formatPrimaryName(option.name) : ''}
        </Option>
      ))}
    </Select>
  )
}
